/**
 * The origins this app may send W3C trace context to (CORE-142).
 *
 * Its own module, with no imports, because it is the piece most worth testing
 * and the only piece of the tracing setup that can be tested without a browser.
 * Get it wrong in one direction and no backend ever sees a `traceparent`; get
 * it wrong in the other and trace ids leak to third parties — and both failures
 * are silent.
 */

/**
 * Build the `propagateTraceHeaderCorsUrls` patterns from the API base URLs the
 * app already has.
 *
 * Derived rather than configured separately so the two cannot drift: the set of
 * places we may send trace context is exactly the set of Oakmore services we
 * call. A second list would be one more thing to forget when a backend is added.
 *
 * Each pattern is **anchored at the start and fully escaped**. An unanchored
 * pattern built from a hostname matches any URL merely *containing* it, which
 * is how a trace id ends up on a request to a third party that happens to carry
 * the string in a query parameter. The dots in a hostname are regex wildcards
 * until escaped, so `auth-dev.oakmore.io` would otherwise also match
 * `auth-devXoakmoreYio`.
 *
 * Invalid and empty URLs are dropped rather than throwing: a malformed entry in
 * an environment file should cost that one origin its propagation, not stop the
 * application from starting.
 */
export function buildPropagationAllowList(urls: readonly (string | undefined)[]): RegExp[] {
    const origins = urls
        .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
        .map((url) => {
            try {
                return new URL(url).origin;
            } catch {
                return null;
            }
        })
        // `new URL('capacitor://localhost').origin` is the string "null" in
        // every browser; treat it as unusable rather than building /^null/,
        // which would match nothing useful and look like it worked.
        .filter((origin): origin is string => origin !== null && origin !== 'null');

    return Array.from(new Set(origins)).map(
        (origin) => new RegExp('^' + origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
    );
}
